// Rahmen der Anwendung: Seitenleiste am Desktop, Tab-Leiste auf dem Telefon.
// Welche der beiden sichtbar ist, entscheidet allein das CSS bei 820 px.

import { useState, type ReactNode } from "react";
import { useZustand, type Sicht } from "../zustand.tsx";
import type { Portfolio } from "../typen.ts";

interface Eintrag { schluessel: string; text: string; symbol: string; bald?: boolean }

const NAVIGATION: { bereich: string; eintraege: Eintrag[] }[] = [
  { bereich: "Übersicht", eintraege: [{ schluessel: "dashboard", text: "Dashboard", symbol: "📊" }] },
  { bereich: "Immobilien", eintraege: [
    { schluessel: "objekte", text: "Objekte", symbol: "🏢" },
    { schluessel: "einheiten", text: "Einheiten", symbol: "🚪" },
    { schluessel: "bald:eigentuemer", text: "Eigentümer", symbol: "👤", bald: true },
  ] },
  { bereich: "Zustand", eintraege: [
    { schluessel: "maengel", text: "Mängelliste", symbol: "⚠️" },
    { schluessel: "inventar", text: "Inventar", symbol: "📦" },
    { schluessel: "vorlage", text: "Inventar-Vorlage", symbol: "📑" },
    { schluessel: "sanierung", text: "Sanierung", symbol: "🛠️" },
  ] },
  { bereich: "Verwaltung", eintraege: [
    { schluessel: "mieter", text: "Mieter", symbol: "👥" },
    { schluessel: "vertraege", text: "Mietverträge", symbol: "📋" },
  ] },
  { bereich: "Finanzen", eintraege: [
    { schluessel: "finanzen", text: "Finanzübersicht", symbol: "💰" },
    { schluessel: "finanzen/buchungen", text: "Einnahmen & Ausgaben", symbol: "💶" },
    { schluessel: "finanzen/konten", text: "Bankkonten", symbol: "🏦" },
    { schluessel: "finanzen/darlehen", text: "Darlehen", symbol: "🏛️" },
    { schluessel: "bald:nk", text: "Nebenkosten", symbol: "🧾", bald: true },
  ] },
  { bereich: "Sonstiges", eintraege: [
    { schluessel: "aufgaben", text: "Aufgaben", symbol: "✅" },
    { schluessel: "dokumente", text: "Dokumente", symbol: "📁" },
    { schluessel: "kommunikation", text: "Kommunikation", symbol: "💬" },
    { schluessel: "konto", text: "Konto & Daten", symbol: "💾" },
  ] },
];

/** Die vier Tabs unten plus „Mehr“. Alles Übrige steckt im Mehr-Blatt. */
const TABS = [
  { schluessel: "dashboard", text: "Start", symbol: "📊" },
  { schluessel: "objekte", text: "Objekte", symbol: "🏢" },
  { schluessel: "maengel", text: "Mängel", symbol: "⚠️" },
  { schluessel: "inventar", text: "Inventar", symbol: "📦" },
];

const alsSicht = (schluessel: string): Sicht => {
  if (schluessel.startsWith("bald:")) return { seite: "bald", thema: schluessel.slice(5) };
  if (schluessel.startsWith("finanzen")) {
    return { seite: "finanzen", bereich: schluessel.split("/")[1] };
  }
  return { seite: schluessel } as Sicht;
};

/** Welcher Navigationspunkt gilt als aktiv — Detailseiten färben ihren Bereich. */
function aktiverPunkt(sicht: Sicht): string {
  if (sicht.seite === "objekt") return "objekte";
  if (sicht.seite === "einheit") return "einheiten";
  if (sicht.seite === "bald") return `bald:${sicht.thema}`;
  if (sicht.seite === "finanzen") {
    return !sicht.bereich || sicht.bereich === "uebersicht"
      ? "finanzen" : `finanzen/${sicht.bereich}`;
  }
  return sicht.seite;
}

function zaehler(schluessel: string, bestand: Portfolio | null): { wert: number; alarm: boolean } | null {
  if (!bestand) return null;
  switch (schluessel) {
    case "objekte":
      return { wert: bestand.objekte.length, alarm: false };
    case "maengel": {
      const offen = bestand.objekte.flatMap((o) => o.maengel).filter((m) => !m.erledigt);
      return offen.length === 0 ? null
        : { wert: offen.length, alarm: offen.some((m) => m.prio === "hoch") };
    }
    case "inventar": {
      const kritisch = bestand.kennzahlen.inventarKritisch;
      return kritisch === 0 ? null : { wert: kritisch, alarm: false };
    }
    case "sanierung": {
      const offen = bestand.objekte.flatMap((o) => o.sanierung).filter((p) => p.status !== "fertig");
      return offen.length === 0 ? null : { wert: offen.length, alarm: false };
    }
    case "aufgaben": {
      const { aufgabenOffen, aufgabenUeberfaellig } = bestand.uebersicht;
      return aufgabenOffen === 0 ? null
        : { wert: aufgabenOffen, alarm: aufgabenUeberfaellig > 0 };
    }
    case "dokumente": {
      const { dokumenteAbgelaufen, dokumenteLaufenAus } = bestand.uebersicht;
      const auffaellig = dokumenteAbgelaufen + dokumenteLaufenAus;
      return auffaellig === 0 ? null : { wert: auffaellig, alarm: dokumenteAbgelaufen > 0 };
    }
    default: return null;
  }
}

export function Huelle({ children }: { children: ReactNode }) {
  const { sicht, gehe, bestand, meldung, offline } = useZustand();
  const [mehrOffen, setzeMehrOffen] = useState(false);
  const aktiv = aktiverPunkt(sicht);

  const offeneMaengel = bestand
    ? bestand.objekte.flatMap((o) => o.maengel).filter((m) => !m.erledigt).length
    : 0;

  return (
    <>
      <div className="app">
        <nav className="side">
          <div className="side-brand"><span className="dot" /><b>Immofolio</b></div>
          {NAVIGATION.map((bereich) => (
            <div key={bereich.bereich}>
              <div className="side-sec">{bereich.bereich}</div>
              {bereich.eintraege.map((e) => {
                const z = zaehler(e.schluessel, bestand);
                return (
                  <button
                    key={e.schluessel}
                    className={`side-item ${aktiv === e.schluessel ? "on" : ""}`}
                    onClick={() => gehe(alsSicht(e.schluessel))}
                  >
                    <span className="ico">{e.symbol}</span>
                    {e.text}
                    {e.bald ? <span className="soon">BALD</span> : null}
                    {z ? <span className={`count ${z.alarm ? "alert" : ""}`}>{z.wert}</span> : null}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="content">
          <div className="topbar">
            <div className="topbar-right">
              <button className="pill-btn" onClick={() => gehe({ seite: "konto" })} title="Konto & Daten">
                👤
              </button>
            </div>
          </div>
          <main className="main">
            {offline ? (
              <div className="banner warn">
                <span>
                  Keine Verbindung zum Server. Die angezeigten Zahlen können
                  veraltet sein, Änderungen werden nicht gespeichert.
                </span>
              </div>
            ) : null}
            {children}
          </main>
        </div>
      </div>

      <nav className="tabbar">
        {TABS.map((t) => (
          <button key={t.schluessel} className={`tb ${aktiv === t.schluessel ? "on" : ""}`}
            onClick={() => gehe(alsSicht(t.schluessel))}>
            <span className="tb-ico">
              {t.symbol}
              {t.schluessel === "maengel" && offeneMaengel > 0
                ? <span className="tb-badge">{offeneMaengel}</span> : null}
            </span>
            <span className="tb-lbl">{t.text}</span>
          </button>
        ))}
        <button className="tb" onClick={() => setzeMehrOffen(true)}>
          <span className="tb-ico">☰</span>
          <span className="tb-lbl">Mehr</span>
        </button>
      </nav>

      {mehrOffen ? <MehrBlatt aufSchliessen={() => setzeMehrOffen(false)} /> : null}

      {meldung
        ? <div className={`streifen ${meldung.fehler ? "fehler" : ""}`}>{meldung.text}</div>
        : null}
    </>
  );
}

/** Alles, was mobil nicht in die Tab-Leiste passt — abgeleitet aus derselben
 *  Navigationsliste, damit eine neue Seite nur an einer Stelle einzutragen ist. */
function MehrBlatt({ aufSchliessen }: { aufSchliessen: () => void }) {
  const { gehe } = useZustand();
  const inTabs = TABS.map((t) => t.schluessel);
  const rest = NAVIGATION.flatMap((b) => b.eintraege)
    .filter((e) => !inTabs.includes(e.schluessel))
    .sort((a, b) => Number(Boolean(a.bald)) - Number(Boolean(b.bald)));

  return (
    <div className="overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) aufSchliessen(); }}>
      <div className="sheet">
        <div className="sheet-hd">
          <h3>Mehr</h3>
          <button className="x" onClick={aufSchliessen} aria-label="Schließen">✕</button>
        </div>
        <div className="sheet-body">
          <div className="more-list">
            {rest.map((e) => (
              <button key={e.schluessel} className="more-item"
                onClick={() => { gehe(alsSicht(e.schluessel)); aufSchliessen(); }}>
                <span className="ico">{e.symbol}</span>
                <span>{e.text}</span>
                {e.bald
                  ? <span className="more-soon">BALD</span>
                  : <span className="more-arrow">›</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
